/**
 * Widget Config Loader Service
 *
 * Загружает конфигурацию виджета из API по frontend token.
 * Используется при token-only инициализации виджета.
 *
 * API endpoint: GET /api/public/widget/config?token=xxx
 */

/**
 * Загрузить конфигурацию виджета из API
 *
 * @param {string} apiUrl - Base URL API (без /api)
 * @param {string} token - Frontend access token
 * @returns {Promise<Object>} Конфигурация виджета
 * @throws {Error} Если токен невалидный или API недоступен
 */
export async function loadWidgetConfig(apiUrl, token) {
  // Нормализовать URL (убрать trailing slash)
  const baseUrl = apiUrl.replace(/\/+$/, '');

  // Определить путь к API (добавить /api если его нет)
  const apiPath = baseUrl.endsWith('/api') ? '' : '/api';
  const url = `${baseUrl}${apiPath}/public/widget/config?token=${encodeURIComponent(token)}`;

  console.log('[WidgetConfigLoader] Loading config from:', url);

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}`;

      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.message || errorMessage;
      } catch {
        // Не JSON, используем текст
        if (errorText) {
          errorMessage = errorText;
        }
      }

      throw new Error(`Failed to load widget config: ${errorMessage}`);
    }

    const config = await response.json();
      console.log('[WidgetConfigLoader] Config loaded successfully:', {
      organizationId: config.organizationId,
      inventorySlug: config.inventorySlug,
      version: config.version,
    });

    return config;
  } catch (error) {
    console.error('[WidgetConfigLoader] Error loading config:', error);
    throw error;
  }
}

/**
 * Преобразовать API конфиг в формат mount() параметров
 *
 * Маппит PublicWidgetConfigResponseDto → config для B2BDemo.mount()
 *
 * @param {Object} apiConfig - Конфигурация из API
 * @param {string} apiUrl - Base URL API
 * @param {string} token - Frontend token
 * @returns {Object} Конфигурация для mount()
 */
export function mapApiConfigToMountConfig(apiConfig, apiUrl, token) {
  console.log('[WidgetConfigLoader] mapApiConfigToMountConfig input:', {
    inventorySlug: apiConfig.inventorySlug,
    organizationId: apiConfig.organizationId,
  });
  // Все потребители mountConfig (widget.jsx setMountParams в embed flow,
  // showroomTokenResolver в standalone+token flow) spread-ят этот объект в
  // window.b2bMountParams целиком — поэтому ВСЕ значимые поля должны быть
  // здесь, без cherry-pick whitelist'ов на стороне потребителей.
  //
  // Field rationale (что забываем при ленивых правках):
  //   • workflow/agentId — без них агент на бэке резолвится в MAIN_WORKFLOW
  //     (default Demo) даже когда виджет визуально брендирован под Andys/Autodis.
  //     `config.WORKFLOW_TYPE` / `AGENT_ID` getters читают это первым приоритетом
  //     и передают в backend через sendMessage payload.
  //   • theme/widget* — `VoiceWidgetConnected` в standalone'е (App.jsx) рендерится
  //     БЕЗ widget.jsx mount(), поэтому `--widget-*` CSS-вары не выставлены —
  //     виджет падает на default themes.shiftgears (orange #F25603), если эти
  //     поля не дошли через mountParams (#938).
  //   • dealerSiteUrl — нужен в standalone print-шаблоне для QR-target (#878):
  //     листовка печатается на demo.shiftgears.ai, QR должен вести на сайт дилера.
  //   • dealerLogoUrl — рендерится в Hero pill лендинга и в print template (#884).
  //   • enableShowroomCatalog (#965) — гейтит `/showroom` route + swipe-shell
  //     на `CarLandingPage`. В embed-mode + standalone+token без него
  //     showroom-флоу не активируется.
  const config = {
    apiUrl: apiUrl,
    organizationId: String(apiConfig.organizationId),
    frontendToken: token,
    // NOTE: mode НЕ берётся из API конфига, только из параметров встраивания
    workflow: apiConfig.workflow || undefined,
    agentId: apiConfig.agentId || undefined,
    inventorySlug: apiConfig.inventorySlug || undefined,
    livekitEndpoint: apiConfig.livekitEndpoint || undefined,
  };

  // Appearance → widget style settings
  if (apiConfig.appearance) {
    config.widgetStyle = apiConfig.appearance.style || undefined;
    config.widgetThemeMode = apiConfig.appearance.themeMode || undefined;
    config.widgetAccent = apiConfig.appearance.accent;
    config.widgetGlass = apiConfig.appearance.glass;
  }

  // Colors → theme object
  if (apiConfig.colors) {
    config.theme = {
      primaryColor: apiConfig.colors.primary,
      primaryHover: apiConfig.colors.primaryHover,
      accentColor: apiConfig.colors.accent,
      bgColor: apiConfig.colors.bg,
      textColor: apiConfig.colors.text,
      specialOffersColor: apiConfig.colors.specialOffers,
    };
    // Удалить undefined значения
    Object.keys(config.theme).forEach(key => {
      if (config.theme[key] === undefined) {
        delete config.theme[key];
      }
    });
    if (Object.keys(config.theme).length === 0) {
      delete config.theme;
    }
  }

  // Typography
  if (apiConfig.typography) {
    config.theme = config.theme || {};
    if (apiConfig.typography.fontFamily) {
      config.theme.fontFamily = apiConfig.typography.fontFamily;
    }
    if (apiConfig.typography.fontSize) {
      config.theme.fontSize = apiConfig.typography.fontSize;
    }
  }

  // Spacing
  if (apiConfig.spacing) {
    config.theme = config.theme || {};
    if (apiConfig.spacing.borderRadius) {
      config.theme.borderRadius = apiConfig.spacing.borderRadius;
    }
    if (apiConfig.spacing.cardRadius) {
      config.theme.cardRadius = apiConfig.spacing.cardRadius;
    }
    if (apiConfig.spacing.buttonRadius) {
      config.theme.buttonRadius = apiConfig.spacing.buttonRadius;
    }
    if (apiConfig.spacing.spacingSm) {
      config.theme.spacingSm = apiConfig.spacing.spacingSm;
    }
    if (apiConfig.spacing.spacingMd) {
      config.theme.spacingMd = apiConfig.spacing.spacingMd;
    }
    if (apiConfig.spacing.spacingLg) {
      config.theme.spacingLg = apiConfig.spacing.spacingLg;
    }
  }

  // #1440 — the `{dealer}` name in the contact-consent text. Taken ONLY from
  // the API widget config: b2b-api records the consent wording with this same
  // field (token → widget config → contact.companyName). `contactInfo` below
  // can be overridden by URL params / iframe and script data-attributes, so the
  // consent box must not read it — the visitor would see one name and the log
  // would store another.
  config.consentDealerName = apiConfig.contact?.companyName?.trim() || null;

  // Contact Info
  if (apiConfig.contact) {
    config.contactInfo = {
      phone: apiConfig.contact.phone,
      email: apiConfig.contact.email,
      whatsapp: apiConfig.contact.whatsapp,
      telegram: apiConfig.contact.telegram,
      hoursMon: apiConfig.contact.hoursMon,
      hoursTue: apiConfig.contact.hoursTue,
      hoursWed: apiConfig.contact.hoursWed,
      hoursThu: apiConfig.contact.hoursThu,
      hoursFri: apiConfig.contact.hoursFri,
      hoursSat: apiConfig.contact.hoursSat,
      hoursSun: apiConfig.contact.hoursSun,
      companyName: apiConfig.contact.companyName,
      companyDescription: apiConfig.contact.companyDescription,
    };

    // Маппинг locations → location1/location2
    if (apiConfig.contact.locations && apiConfig.contact.locations.length > 0) {
      const loc1 = apiConfig.contact.locations[0];
      config.contactInfo.location1Name = loc1.name;
      config.contactInfo.location1Address = loc1.address;
      config.contactInfo.location1City = loc1.city;
      config.contactInfo.location1Country = loc1.country;
      config.contactInfo.location1Maps = loc1.mapsUrl;

      if (apiConfig.contact.locations.length > 1) {
        const loc2 = apiConfig.contact.locations[1];
        config.contactInfo.location2Name = loc2.name;
        config.contactInfo.location2Address = loc2.address;
        config.contactInfo.location2City = loc2.city;
        config.contactInfo.location2Country = loc2.country;
        config.contactInfo.location2Maps = loc2.mapsUrl;
      }
    }

    // Удалить undefined значения
    Object.keys(config.contactInfo).forEach(key => {
      if (config.contactInfo[key] === undefined) {
        delete config.contactInfo[key];
      }
    });
    if (Object.keys(config.contactInfo).length === 0) {
      delete config.contactInfo;
    }
  }

  // Activation
  if (apiConfig.activation) {
    config.activationKey = apiConfig.activation.key === null ? null : (apiConfig.activation.key || undefined);
    config.activationMode = apiConfig.activation.mode || 'localStorage';
    config.activationQueryParamName = apiConfig.activation.queryParamName || undefined;
    config.activationQueryParamValue = apiConfig.activation.queryParamValue || undefined;
    config.hideSelector = apiConfig.activation.hideSelectors || undefined;
    config.catalogPathPattern = apiConfig.activation.catalogPathPattern || undefined;
  }

  // Header/Footer
  if (apiConfig.headerFooter) {
    config.showHeader = apiConfig.headerFooter.showHeader;
    config.showFooter = apiConfig.headerFooter.showFooter;
  }

  // Features
  if (apiConfig.features) {
    config.showWhatsApp = apiConfig.features.showWhatsAppButton;
    // #1493 — Telegram рядом с WhatsApp; хендл ниже из contact.telegram.
    config.showTelegram = apiConfig.features.showTelegramButton;
    config.showDutyFreePrices = apiConfig.features.showDutyFreePrices;
    config.showRegistrationText = apiConfig.features.showRegistrationText;
    if (apiConfig.features.showSaleBadge !== undefined) {
      config.showSaleBadge = apiConfig.features.showSaleBadge;
    }
    config.enableServiceCenter = apiConfig.features.enableServiceCenter ?? false;
    // Issue #965 — showroom catalog flow (Autodis-style swipe between
    // mini-landings + /showroom). Off by default so dealers without
    // the flag keep the pre-#965 standalone landing.
    config.enableShowroomCatalog = apiConfig.features.enableShowroomCatalog ?? false;
    config.enableRentPrefill = apiConfig.features.enableRentPrefill ?? false;
    // Issue #1353 — text-only виджет: гейтит ВСЕ точки входа в голос
    // (микрофон в collapsed-баре, voice/chat тумблер в шапке чата,
    // программный open('voice'), restore в voice после навигации).
    // Default false — дилеры без флага сохраняют голос.
    config.disableVoiceMode = apiConfig.features.disableVoiceMode ?? false;
    // Issue #972 — дублировать submit форм виджета (Check Availability,
    // Finance Application) в CRM через POST /api/widget/lead-capture.
    // Раньше поле не маппилось → window.b2bMountParams.captureFormsToCrm был
    // всегда undefined → виджет видел default false независимо от админки.
    config.captureFormsToCrm = apiConfig.features.captureFormsToCrm ?? false;
    // Search Bar (issue #913) — Demo Showcase toggle "Search Bar" в разделе
    // Catalog Display сохраняет в `features.showSearchBar` + `features.searchBarMode`,
    // но раньше эти поля не маппились → `window.b2bMountParams.showSearchBar`
    // был всегда undefined → `searchCarInventory` короткозамыкался на disabled.
    // Симптом: и legacy in-catalog SearchBar в `<CarsPage>`, и search-only embed
    // в registry (#860) молчали независимо от того, что admin выставлял в форме.
    if (apiConfig.features.showSearchBar !== undefined) {
      config.showSearchBar = apiConfig.features.showSearchBar;
    }
    if (apiConfig.features.searchBarMode !== undefined) {
      config.searchBarMode = apiConfig.features.searchBarMode;
    }
  }

  // NOTE: search-only embed auto-enable (была в #913) удалена в #932.
  // Раньше: если в `apiConfig.embeds[]` есть mode='search-only' →
  // `showSearchBar=true`. Это leak'ало в `<CarsPage>` — global flag
  // активировал и in-catalog SearchBar (нежелательное побочное
  // включение). Теперь Search registry component force-bypass'ит
  // disabled gate в `services/api.js` через `searchCarInventory(q, { force: true })`,
  // marker presence сам по себе explicit opt-in. Global flag
  // `features.showSearchBar` остаётся exclusively для in-catalog
  // SearchBar в CarsPage (admin контролирует через Demo Showcase toggle).

  // WhatsApp number для кнопки в виджете
  // Приоритет: whatsapp.number (dedicated settings) > contact.whatsapp (contact info)
  config.whatsAppNumber = apiConfig.whatsapp?.number || apiConfig.contact?.whatsapp;

  // Telegram-хендл для кнопки в виджете (#1493). Отдельной JSONB-группы, как у
  // WhatsApp, нет — хендл живёт в contact.telegram.
  config.telegramHandle = apiConfig.contact?.telegram;

  // Branding (issues #865 / #884 / #957) — title + dealer logo + share tagline
  if (
    apiConfig.branding?.title ||
    apiConfig.branding?.logoUrl ||
    apiConfig.branding?.shareTagline
  ) {
    config.branding = {};
    if (apiConfig.branding.title) {
      config.branding.title = apiConfig.branding.title;
      // Дублируем на верхний уровень — runtimeConfig.WIDGET_TITLE читает
      // mount.widgetTitle, не branding.title. Без этого моста сохранённый
      // в branding.title заголовок не доходил до виджета (всегда 'Ask AI').
      // Тот же паттерн, что logoUrl → dealerLogoUrl и shareTagline ниже.
      config.widgetTitle = apiConfig.branding.title;
    }
    if (apiConfig.branding.logoUrl) {
      config.branding.logoUrl = apiConfig.branding.logoUrl;
      // Дублируем на верхний уровень — runtimeConfig.DEALER_LOGO_URL читает
      // mount.dealerLogoUrl, и это самый дешёвый путь подключения logo
      // дилера в Hero pill / print template без правок десятка getter-ов.
      config.dealerLogoUrl = apiConfig.branding.logoUrl;
    }
    if (apiConfig.branding.shareTagline) {
      // Аналогично logo — кладём на верхний уровень для runtimeConfig.SHARE_TAGLINE.
      // Используется в ShareModal на car detail (issue #957).
      config.shareTagline = apiConfig.branding.shareTagline;
    }
  }

  // Dealer logo URL — попадает на mini-car landing pill через
  // mount.dealerLogoUrl → runtimeConfig.DEALER_LOGO_URL → CarLandingPage.
  // Должен быть АБСОЛЮТНЫЙ HTTPS URL (хостится на CDN дилера или нашем
  // CDN), потому что в widget-overlay режиме relative-path резолвится
  // против host origin → 404. См. backend issue #865 про widget-config
  // поле, и #897 про widget-side починку. Без явного значения logo-pill
  // не рендерится (CarLandingPage условный рендер).
  if (apiConfig.branding?.logoUrl) {
    config.dealerLogoUrl = apiConfig.branding.logoUrl;
  }

  // WhatsApp settings
  if (apiConfig.whatsapp) {
    config.whatsappSettings = {
      enabled: apiConfig.whatsapp.enabled,
      number: apiConfig.whatsapp.number,
      message: apiConfig.whatsapp.message,
    };
    // Удалить undefined значения
    Object.keys(config.whatsappSettings).forEach(key => {
      if (config.whatsappSettings[key] === undefined) {
        delete config.whatsappSettings[key];
      }
    });
    if (Object.keys(config.whatsappSettings).length === 0) {
      delete config.whatsappSettings;
    }
  }

  // Duty Free settings
  if (apiConfig.dutyFree) {
    config.dutyFreeSettings = {
      enabled: apiConfig.dutyFree.enabled,
      vatRate: apiConfig.dutyFree.vatRate,
      label: apiConfig.dutyFree.label,
      tooltipTitle: apiConfig.dutyFree.tooltipTitle,
      tooltipText: apiConfig.dutyFree.tooltipText,
      buttonText: apiConfig.dutyFree.buttonText,
      priceLabel: apiConfig.dutyFree.priceLabel,
    };
    // Удалить undefined значения
    Object.keys(config.dutyFreeSettings).forEach(key => {
      if (config.dutyFreeSettings[key] === undefined) {
        delete config.dutyFreeSettings[key];
      }
    });
    if (Object.keys(config.dutyFreeSettings).length === 0) {
      delete config.dutyFreeSettings;
    }
  }

  // Why Buy settings → flat mount params expected by B2BDemo.mount()
  if (apiConfig.whyBuy) {
    config.showWhyBuyBlock = apiConfig.whyBuy.enabled ?? false;
    config.whyBuyTitle = apiConfig.whyBuy.title;
    config.whyBuyItem1 = apiConfig.whyBuy.item1;
    config.whyBuyItem2 = apiConfig.whyBuy.item2;
    config.whyBuyItem3 = apiConfig.whyBuy.item3;
    config.whyBuyItem4 = apiConfig.whyBuy.item4;
    config.whyBuyItem5 = apiConfig.whyBuy.item5;
  }

  // Form widget config (#1081/#1082) — standalone `data-shiftgears="form"` embed.
  // Backend exposes `formConfig` at the top level of the public config response
  // (WidgetFormConfig: enabled/defaultChannel/showChannelChooser/successMessage/
  // handoverCtaText/forms). Flatten to the whitelisted `form*` mount params
  // (precedent: captureFormsToCrm above). Per-embed marker `data-*` attrs still
  // override these globals at runtime (LeadFormEmbed reads attrs first).
  if (apiConfig.formConfig) {
    const fc = apiConfig.formConfig;
    if (fc.enabled !== undefined) config.formEnabled = fc.enabled;
    if (fc.defaultChannel) config.formDefaultChannel = fc.defaultChannel;
    if (fc.showChannelChooser !== undefined) config.formShowChannelChooser = fc.showChannelChooser;
    if (fc.successMessage) config.formSuccessBanner = fc.successMessage;
    if (fc.handoverCtaText) config.formHandoverCtaText = fc.handoverCtaText;
    // NOTE: `formConfig.forms` (the per-form-type override bag) is intentionally
    // NOT mapped here. It was previously flattened to `formCustomFieldsByType`,
    // but that mount param is not whitelisted in widget.jsx and nothing consumes
    // it (LeadFormEmbed reads the flat `formCustomFields` list). Mapping it was a
    // dead no-op, so it is removed (#1081 review). Per-form-type overrides are a
    // future feature; wire them end-to-end (loader → widget.jsx whitelist →
    // runtimeConfig getter → LeadFormEmbed) when actually needed.
  }

  // Form templates (#1087 / #1123) — resolved registry (built-in ⊕ org
  // overrides) served at the TOP LEVEL of the public config as `formTemplates`
  // (not under `formConfig`). Passed through verbatim so LeadFormEmbed can
  // resolve a template by `code`. Flattened to the whitelisted `formTemplates`
  // mount param (whitelisted in widget.jsx → FORM_TEMPLATES getter).
  if (Array.isArray(apiConfig.formTemplates) && apiConfig.formTemplates.length > 0) {
    config.formTemplates = apiConfig.formTemplates;
  }

  // Suggestions - настройки авто-предложений в чате (#752)
  // Feature flag: default to false; enable per-org via widget config UI when ready.
  if (apiConfig.suggestions) {
    config.suggestionsEnabled = apiConfig.suggestions.enabled ?? false;
    config.suggestionsMaxVisible = apiConfig.suggestions.maxVisible;
    config.suggestionsAccentColor = apiConfig.suggestions.accentColor;
    config.suggestionsBubbleVariant = apiConfig.suggestions.bubbleVariant;
  }

  // BF Package settings → flat mount params
  if (apiConfig.bfPackage) {
    config.showBfPackage = apiConfig.bfPackage.enabled ?? false;
    if (apiConfig.bfPackage.annualRate != null) config.bfAnnualRate = apiConfig.bfPackage.annualRate;
    if (apiConfig.bfPackage.installmentMonths != null) config.bfLoanTermMonths = apiConfig.bfPackage.installmentMonths;
    if (apiConfig.bfPackage.balloonPercent != null) config.bfBalloonPercent = apiConfig.bfPackage.balloonPercent;
    if (apiConfig.bfPackage.balloonMonth != null) config.bfBalloonMonth = apiConfig.bfPackage.balloonMonth;
    if (apiConfig.bfPackage.maxFinancing != null) config.bfMaxFinancing = apiConfig.bfPackage.maxFinancing;
    if (apiConfig.bfPackage.title) config.bfPackageTitle = apiConfig.bfPackage.title;
    if (apiConfig.bfPackage.badge1) config.bfPackageBadge1 = apiConfig.bfPackage.badge1;
    if (apiConfig.bfPackage.badge2) config.bfPackageBadge2 = apiConfig.bfPackage.badge2;
    if (apiConfig.bfPackage.badge3) config.bfPackageBadge3 = apiConfig.bfPackage.badge3;
    if (apiConfig.bfPackage.disclaimer) config.bfPackageDisclaimer = apiConfig.bfPackage.disclaimer;
    if (apiConfig.bfPackage.ctaText) config.bfPackageCtaText = apiConfig.bfPackage.ctaText;
    if (apiConfig.bfPackage.badgeColor) config.bfBadgeColor = apiConfig.bfPackage.badgeColor;
    if (apiConfig.bfPackage.sliderColor) config.bfSliderColor = apiConfig.bfPackage.sliderColor;
  }

  // AI Consultation settings → flat mount params (only enabled toggles).
  // Nested object pattern per MR #650: aiConsultation: { checkAvailability: {enabled}, bfFinance: {enabled} }
  // Greeting текст НЕ хранится здесь — он в b2b_agent/livekit_builder/default_phrases.py
  // и выбирается агентом по launch_context.action ('ask_about_finance' | 'check_availability').
  if (apiConfig.aiConsultation) {
    if (apiConfig.aiConsultation.checkAvailability?.enabled !== undefined) {
      config.checkAvailabilityAiConsultationEnabled = apiConfig.aiConsultation.checkAvailability.enabled;
    }
    if (apiConfig.aiConsultation.bfFinance?.enabled !== undefined) {
      config.bfFinanceAiConsultationEnabled = apiConfig.aiConsultation.bfFinance.enabled;
    }
  }

  // Website Pages - страницы дилерского сайта для навигации (chat-only режим)
  if (apiConfig.websitePages?.pages && apiConfig.websitePages.pages.length > 0) {
    config.websitePages = apiConfig.websitePages.pages;
    console.log('[WidgetConfigLoader] Website pages loaded:', config.websitePages.length);
  }

  // Website Path Prefix - префикс пути для навигации (например '/andys' для eezyworks.com/andys/)
  if (apiConfig.websitePages?.pathPrefix) {
    config.websitePathPrefix = apiConfig.websitePages.pathPrefix;
    console.log('[WidgetConfigLoader] Website path prefix:', config.websitePathPrefix);
  }

  // Legal pages (#711) — Terms of Service / Privacy Policy URLs из b2b-api.
  // Попадают в mount params (highest priority в config.TERMS_URL / PRIVACY_URL
  // resolution chain), override-ят env/default. Если apiConfig.legalPages не задан
  // или поле пустое — chain fallback'нется на env/legalDefaults.js.
  if (apiConfig.legalPages?.termsUrl) {
    config.termsUrl = apiConfig.legalPages.termsUrl;
    console.log('[WidgetConfigLoader] Terms URL from API:', config.termsUrl);
  }
  if (apiConfig.legalPages?.privacyUrl) {
    config.privacyUrl = apiConfig.legalPages.privacyUrl;
    console.log('[WidgetConfigLoader] Privacy URL from API:', config.privacyUrl);
  }

  // Dealer Site URL — приоритет:
  //   1. apiConfig.primaryOrigin — явное поле «основной сайт дилера» (issue #879).
  //      Backend валидирует что primaryOrigin ∈ allowedOrigins. Single string,
  //      может быть null. Используется для print URL'ов из b2b-ui и QR-target.
  //   2. apiConfig.allowedOrigins[0] — fallback для legacy токенов без явного
  //      primaryOrigin. На многодоменных токенах (prod + staging + localhost)
  //      [0] непредсказуем — поэтому primaryOrigin лучше.
  //   3. undefined — getter `config.DEALER_SITE_URL` сам fallback'нет на
  //      window.location.origin в widget mode.
  if (typeof apiConfig.primaryOrigin === 'string' && apiConfig.primaryOrigin.length > 0) {
    config.dealerSiteUrl = apiConfig.primaryOrigin;
    console.log('[WidgetConfigLoader] Dealer site URL resolved from primaryOrigin:', config.dealerSiteUrl);
  } else if (Array.isArray(apiConfig.allowedOrigins) && apiConfig.allowedOrigins.length > 0) {
    config.dealerSiteUrl = apiConfig.allowedOrigins[0];
    console.log('[WidgetConfigLoader] Dealer site URL resolved from allowedOrigins[0] (legacy fallback):', config.dealerSiteUrl);
  }

  // Widget embeds (#828) — per-embed конфигурация живёт в widget-config для admin UI.
  // Виджет при runtime к этому полю НЕ обращается: каждый embed-скрипт на хост-странице
  // самодостаточен (получает свой mode top-level + settings через overrides в mountWithToken).
  // Поэтому embeds здесь не пробрасываются в mount config.

  // Удалить все undefined значения на верхнем уровне
  Object.keys(config).forEach(key => {
    if (config[key] === undefined) {
      delete config[key];
    }
  });

  console.log('[WidgetConfigLoader] Mapped config:', {
    organizationId: config.organizationId,
    inventorySlug: config.inventorySlug,
    hasTheme: !!config.theme,
    hasContactInfo: !!config.contactInfo,
  });

  return config;
}

export default {
  loadWidgetConfig,
  mapApiConfigToMountConfig,
};
// Trigger CI
